import { useEffect, useState } from 'react';
import type { Operaciones } from '../../api/operaciones';
import type { Recurso, Servicio, Zona } from '../../api/ops/pagos';

/**
 * Lo que las pantallas de Pagos leen del negocio: sus palabras (`vocabulary`, G214: «reservado» en ocio nocturno, nunca
 * «mesa» fija), si tiene Citas (para «Citas de varios servicios», G41) y su moneda. Si algo falla, palabras neutras.
 */
export interface NegocioPagos {
    /** Palabra del recurso del sector («mesa», «reservado», «sillón»…) o «recurso». */
    recurso: string;
    /** «reserva» o «cita» (o lo que diga el vocabulario). */
    compromiso: string;
    citas: boolean;
    reservas: boolean;
    moneda: string;
}

export const NEGOCIO_NEUTRO: NegocioPagos = { recurso: 'recurso', compromiso: 'reserva', citas: false, reservas: false, moneda: 'EUR' };

export function useNegocioPagos(ops: Operaciones, negocio: string): NegocioPagos {
    const [datos, setDatos] = useState<NegocioPagos>(NEGOCIO_NEUTRO);
    useEffect(() => {
        let vivo = true;
        Promise.all([ops.getBusinessConVersion(negocio).catch(() => null), ops.listEntitlements(negocio).catch(() => null)])
            .then(([n, bloques]) => {
                if (!vivo) {
                    return;
                }
                const codigos = (bloques?.entitlements ?? []).map((e) => e.product_code);
                const v = n?.datos.vocabulary ?? {};
                const palabra = (clave: string, porDefecto: string) => {
                    const p = v[clave]?.trim();
                    return p === undefined || p === '' ? porDefecto : p.toLocaleLowerCase('es');
                };
                const citas = codigos.includes('citas');
                setDatos({
                    recurso: palabra('resource', 'recurso'),
                    compromiso: palabra('appointment', citas && !codigos.includes('reservas') ? 'cita' : 'reserva'),
                    citas,
                    reservas: codigos.includes('reservas'),
                    moneda: n?.datos.currency ?? 'EUR',
                });
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio]);
    return datos;
}

/** «reservas», «mesas»: el plural de una palabra en español (vocal + s, consonante + es). */
export function plural(palabra: string): string {
    return /[aeiouáéó]$/i.test(palabra) ? `${palabra}s` : `${palabra}es`;
}

export const mayuscula = (t: string): string => t.charAt(0).toLocaleUpperCase('es') + t.slice(1);

/**
 * Los sitios a los que se asigna una política (G36, G281): zonas y recursos de Reservas (las estancias son zonas
 * `stay`) y servicios de Citas, cada uno con la operación de su módulo. Un módulo que aún no existe (404) o que la
 * persona no puede ver da una lista vacía: la pantalla lo dice sin romperse.
 */
export interface Sitios {
    zonas: Zona[];
    recursos: Recurso[];
    servicios: Servicio[];
}

async function todas<T>(pedir: (cursor: string | null) => Promise<{ data: T[]; next_cursor: string | null }>): Promise<T[]> {
    const lista: T[] = [];
    let cursor: string | null = null;
    for (let i = 0; i < 20; i += 1) {
        const pagina = await pedir(cursor);
        lista.push(...pagina.data);
        cursor = pagina.next_cursor;
        if (cursor === null) {
            break;
        }
    }
    return lista;
}

export async function cargarSitios(ops: Operaciones, negocio: string): Promise<Sitios> {
    const [zonas, recursos, servicios] = await Promise.all([
        todas((cursor) => ops.listZones(negocio, { cursor, limit: 100 })).catch(() => [] as Zona[]),
        todas((cursor) => ops.listResources(negocio, { cursor, limit: 100 })).catch(() => [] as Recurso[]),
        todas((cursor) => ops.listServices(negocio, { cursor, limit: 100 })).catch(() => [] as Servicio[]),
    ]);
    return { zonas: zonas.filter((z) => z.status !== 'archived'), recursos: recursos.filter((r) => r.status !== 'archived'), servicios: servicios.filter((s) => s.status !== 'archived') };
}

/** Nombre de un sitio por su referencia (`assignable` de un uso). */
export function nombreDeSitio(sitios: Sitios | null, tipo: string, id: string, n: NegocioPagos): { nombre: string; tipo: string } {
    if (tipo.includes('zone')) {
        const z = sitios?.zonas.find((x) => x.id === id);
        return { nombre: z?.name ?? id, tipo: z?.modality === 'stay' ? 'Estancia' : 'Zona' };
    }
    if (tipo.includes('resource')) {
        const r = sitios?.recursos.find((x) => x.id === id);
        return { nombre: r?.public_name ?? r?.code ?? id, tipo: mayuscula(n.recurso) };
    }
    if (tipo.includes('service')) {
        const s = sitios?.servicios.find((x) => x.id === id);
        return { nombre: s?.current_version.name ?? id, tipo: 'Servicio' };
    }
    return { nombre: id, tipo: 'Sitio' };
}
